import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Alert, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import IntegrationConfigForm from '../integrations/IntegrationConfigForm'
import integrationService from '../../services/integrationService'
import type { IntegrationConfig, IntegrationConfigCreate, IntegrationConfigUpdate } from '../../types/integration'
import { getErrorMessage } from '../../utils/errorHandlers'

const WhatsAppEnotifyConnection: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(true)
  const [existingConfig, setExistingConfig] = useState<IntegrationConfig | null>(null)
  const [error, setError] = useState<string | null>(null)

  const loadConfig = async () => {
    setLoading(true)
    setError(null)
    try {
      const { configs } = await integrationService.getIntegrationsByType('whatsapp', false)
      const entotifyConfig = configs.find((c) => c.provider_name === 'entotify') || null
      setExistingConfig(entotifyConfig)
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load Enotify connection settings'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadConfig()
  }, [])

  const handleSubmit = async (data: IntegrationConfigCreate | IntegrationConfigUpdate) => {
    try {
      if (existingConfig) {
        await integrationService.updateIntegration(existingConfig.id, data as IntegrationConfigUpdate)
        enqueueSnackbar('Enotify connection updated', { variant: 'success' })
      } else {
        await integrationService.createIntegration(data as IntegrationConfigCreate)
        enqueueSnackbar('Enotify connection saved', { variant: 'success' })
      }
      loadConfig()
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to save connection settings'), { variant: 'error' })
      throw err
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box maxWidth={700}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This is the single source of truth for the Enotify WhatsApp API credentials — every WhatsApp message the
        platform sends (order alerts, document notifications, lead follow-ups, OTP) uses this connection. Without a
        saved connection here, the system falls back to the <code>ENOTIFY_*</code> environment variables.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <IntegrationConfigForm
        serviceType="whatsapp"
        providerName="entotify"
        integrationId={existingConfig?.id}
        initialData={
          existingConfig
            ? {
                is_active: existingConfig.is_active,
                is_primary: existingConfig.is_primary,
                priority: existingConfig.priority,
                config: existingConfig.config,
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={loadConfig}
      />
    </Box>
  )
}

export default WhatsAppEnotifyConnection
